import { useEffect, useState } from 'react';
import axios from 'axios';
import { Route, Plus, Trash2, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/ai-management`;

const STRATEGIES = [
  { value: 'cost', label: 'Cost (Cheapest first)', color: 'green' },
  { value: 'quality', label: 'Quality (Best first)', color: 'purple' },
  { value: 'balanced', label: 'Balanced', color: 'blue' },
  { value: 'latency', label: 'Latency (Fastest first)', color: 'orange' },
];

const TASK_TYPES = ['reasoning', 'image_generation', 'video_generation', 'audio_generation', 'avatar_generation'];

export default function RoutingRules() {
  const [rules, setRules] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    rule_name: '',
    task_type: 'image_generation',
    primary_model_id: '',
    fallback_model_ids: [],
    routing_strategy: 'balanced',
    max_latency_ms: '',
    max_retries: 3,
  });

  useEffect(() => {
    fetchModels();
    fetchRules();
  }, []);

  const fetchModels = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/models`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setModels(response.data.filter(m => m.status === 'active'));
    } catch (error) {
      console.error('Error fetching models:', error);
    }
  };

  const fetchRules = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/routing-rules`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRules(response.data);
    } catch (error) {
      console.error('Error fetching rules:', error);
      toast.error('Failed to fetch routing rules');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      const payload = {
        ...formData,
        max_latency_ms: formData.max_latency_ms ? parseInt(formData.max_latency_ms) : null,
      };
      
      await axios.post(`${API}/routing-rules`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Routing rule created');
      setShowDialog(false);
      resetForm();
      fetchRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create rule');
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Delete this routing rule?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/routing-rules/${ruleId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Rule deleted');
      fetchRules();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const resetForm = () => {
    setFormData({
      rule_name: '',
      task_type: 'image_generation',
      primary_model_id: '',
      fallback_model_ids: [],
      routing_strategy: 'balanced',
      max_latency_ms: '',
      max_retries: 3,
    });
  };

  const getStrategyColor = (strategy) => {
    const s = STRATEGIES.find(st => st.value === strategy);
    return s ? s.color : 'gray';
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="text-lg text-slate-600">Loading...</div></div>;

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-2">Routing Rules</h1>
          <p className="text-slate-600">Configure model selection and fallback strategies</p>
        </div>
        <Button onClick={() => { resetForm(); setShowDialog(true); }} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" /> Add Rule
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Rule Name</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Task Type</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Primary Model</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Fallbacks</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Strategy</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {rules.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-8 text-center text-slate-500">
                  <Route className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  No routing rules configured. Click "Add Rule" to create one.
                </td>
              </tr>
            ) : (
              rules.map((rule) => (
                <tr key={rule.rule_id} className="hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-slate-900">{rule.rule_name}</div>
                    <div className="text-xs text-slate-500">Max retries: {rule.max_retries}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded">{rule.task_type}</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-900">{rule.primary_model_name}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {rule.fallback_model_names.length > 0 ? (
                      <div className="space-y-1">
                        {rule.fallback_model_names.slice(0, 2).map((name, i) => (
                          <div key={i} className="text-xs">{i + 1}. {name}</div>
                        ))}
                        {rule.fallback_model_names.length > 2 && (
                          <div className="text-xs text-slate-500">+{rule.fallback_model_names.length - 2} more</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">None</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 bg-${getStrategyColor(rule.routing_strategy)}-100 text-${getStrategyColor(rule.routing_strategy)}-800 text-xs rounded`}>
                      {rule.routing_strategy}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <button onClick={() => handleDelete(rule.rule_id)} className="text-red-600 hover:text-red-800">
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create Routing Rule</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label>Rule Name *</Label>
                <input className="w-full px-3 py-2 border rounded" placeholder="e.g., Video Generation Primary" value={formData.rule_name} onChange={(e) => setFormData({...formData, rule_name: e.target.value})} required />
              </div>

              <div>
                <Label>Task Type *</Label>
                <Select value={formData.task_type} onValueChange={(value) => setFormData({...formData, task_type: value})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TASK_TYPES.map(type => <SelectItem key={type} value={type}>{type}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Primary Model *</Label>
                <Select value={formData.primary_model_id || undefined} onValueChange={(value) => setFormData({...formData, primary_model_id: value})}>
                  <SelectTrigger><SelectValue placeholder="Select primary model" /></SelectTrigger>
                  <SelectContent>
                    {models.filter(m => m.model_type === formData.task_type).length > 0 ? (
                      models.filter(m => m.model_type === formData.task_type).map(model => (
                        <SelectItem key={model.model_id} value={model.model_id}>{model.model_name} ({model.provider_name})</SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-models" disabled>No models available for this type</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Routing Strategy *</Label>
                <Select value={formData.routing_strategy} onValueChange={(value) => setFormData({...formData, routing_strategy: value})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STRATEGIES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Max Latency (ms)</Label>
                  <input className="w-full px-3 py-2 border rounded" type="number" placeholder="5000" value={formData.max_latency_ms} onChange={(e) => setFormData({...formData, max_latency_ms: e.target.value})} />
                </div>
                <div>
                  <Label>Max Retries</Label>
                  <input className="w-full px-3 py-2 border rounded" type="number" min="1" max="10" value={formData.max_retries} onChange={(e) => setFormData({...formData, max_retries: parseInt(e.target.value)})} />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">Create Rule</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
